import React, {useContext, useEffect} from "react"
import Row from "@amzn/meridian/row";
import {PickContext} from "./context/PickContext";
import {PickFilterContext} from "./context/PickFilterContext";
import {
    DWELLING_PICKLIST,
    PICK_STATUS,
    PICK_STATUS_AR_V2,
    PICK_STATUS_DYNAMIC,
    PICK_STATUS_IN_GRID
} from "../util/EntityStatusManager";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import DWPUpload from "../fileupload/DWPUpload";
import {DWPFileContext} from "../fileupload/context/DWPUploadContext";
import Column from "@amzn/meridian/column";
import PickStatusViewV2 from "./PickStatusV2";
import {FormattedMessage} from "react-intl";
import ActionableMetricView from "./ActionableMetricView";
import Tooltip from "@amzn/meridian/tooltip";
import ActionableMetricPairView from "./ActionableMetricPairView";
import {CapabilityContext} from "../global/context/CapabilityContext";
import * as FilterUtil from "../util/FilterUtil"
import AveragePickDurationView from "./AveragePickDurationView";
import {AR_V2, CORE_AMZL} from "../util/Constants";
import StaticMetricView from "./StaticMetricView";
import ScrollableElementWrapper from "../commonModals/ScrollableElementWrapper";
import {useDeviceLayout} from "../common/context/DeviceLayoutContext";
import {AlertContext} from "../alert/context/AlertContext";
import {getDispatchPlanningURL} from "../util/Util";
import Link from "@amzn/meridian/link";
import {PickViewSelectorContext} from "./context/PickViewSelectorContext";
import {
    AVAILABLE, MANUAL_ASSIGNMENT, MANUALLY_PRIORITISED, OPTIMAL,
    PICK_VIEWS,
    PICKLIST_ASSIGNABLE,
    PICKLIST_ASSIGNED,
    PICKLIST_IN_PROGRESS, PICKLIST_LOCKED, TIME_CRITICAL
} from "./constants/Constants";
import {fetchPicklistsInGridBasedOnAppliedFilters} from "../util/FilterUtil";

const PickOverAllStatusV2 = () => {
    const {viewModel} = useContext(PickContext)
    const {alertDispatch} = useContext(AlertContext)
    const {viewModelF, dispatchF} = useContext(PickFilterContext)
    const {viewModelDWP} = useContext(DWPFileContext);
    const {configViewModel} = useContext(CapabilityContext)
    const {viewState} = useContext(PickViewSelectorContext);
    const {headerFont, textFont} = useDeviceLayout()

    /** Create clone of global route list */
    let routeList = Array.from(viewModel.routeList);

    /** Create clone of global available list of picklists in grid per aisle group */
    let picklistsInAisleGroups = Array.from(viewModel.picklistsInAisleGroups);

    /** Create clone of global available list of picklists in grid per work group */
    let picklistsInWorkGroups = viewModel.picklistsInWorkGroups || [];

    /** Apply filters on global route list */
    routeList = FilterUtil.applyGlobalPickFilters(routeList, viewModelF, configViewModel);

    const getPickMetrics = (routes) => {
        const metrics = {
            totalRoutes: routes.length,
            routeNotReady: 0,
            routeNotAssigned: 0,
            routeInProgress: 0,
            routePicked: 0,
            routePartiallyPicked: 0,
            delayedPickRoutesARv2: 0,
            routeCut: 0,
            routeSidelined: 0,
            picklistNotAssigned: 0,
            picklistInProgress: 0,
        }
        routes.forEach(route => {
            /** Route level metrics */
            switch (route.status) {
                case PICK_STATUS.NOT_ASSIGNED:
                    metrics.routeNotAssigned++;
                    break;
                case PICK_STATUS.IN_PROGRESS:
                    metrics.routeInProgress++;
                    break;
                case PICK_STATUS.PARTIALLY_PICKED:
                    metrics.routePartiallyPicked++
                    break;
                case PICK_STATUS.PICKED:
                    metrics.routePicked++;
                    break;
                case PICK_STATUS_DYNAMIC.NOT_READY:
                    metrics.routeNotReady++;
                    break;
                case PICK_STATUS_AR_V2.ROUTE_CUT:
                    metrics.routeCut++;
                    break;
                case PICK_STATUS_AR_V2.SIDELINED:
                    metrics.routeSidelined++;
                    break;
                case PICK_STATUS_AR_V2.DELAYED_PICK:
                    metrics.delayedPickRoutesARv2++;
                    break;
                default:
                    break;
            }
            /** Picklist level metrics */
            route.picklists.forEach(picklist => {
                switch (picklist.status) {
                    case PICK_STATUS.NOT_ASSIGNED:
                        metrics.picklistNotAssigned++;
                        break;
                    case PICK_STATUS.IN_PROGRESS:
                        metrics.picklistInProgress++;
                        break;
                    default:
                        break;
                }
            })
        });

        return metrics
    }

    const getPickStatusInGridMetrics = (picklistsInAisleGroups, picklistsInWorkGroups) => {
        const metrics = {
            availablePicklistsInGrid: 0,
            picklistsAvailableForAssignment: 0,
            picklistsBlockedAssignment: 0,
            optimallyAssignedPicklists: 0,
            manuallyPrioritisedPicklists: 0,
            manuallyAssignedPicklists: 0,
            timeCriticalPicklists: 0,
            dwellingPicklists: 0,
        };
        let filteredData = fetchPicklistsInGridBasedOnAppliedFilters(picklistsInAisleGroups, picklistsInWorkGroups, viewModelF);
        filteredData.picklists.forEach(picklist => {
            metrics.availablePicklistsInGrid++;
            if (picklist.timeCritical) {
                metrics.timeCriticalPicklists++;
            }

            /** Picklist level metrics */
            switch (picklist.status) {
                case PICKLIST_ASSIGNABLE:
                    const aisleGroupStatus = filteredData.picklistToAisleGroupStatusMap.get(picklist);
                    if (picklist.isDwelling) {
                        // Dwelling currently applies only to work groups and is not applicable to aisle groups.
                        metrics.dwellingPicklists++;
                        metrics.picklistsAvailableForAssignment++;
                    } else if (aisleGroupStatus && aisleGroupStatus !== AVAILABLE) {
                        metrics.picklistsBlockedAssignment++;
                    } else {
                        metrics.picklistsAvailableForAssignment++;
                    }
                    break;
                case PICKLIST_ASSIGNED:
                case PICKLIST_IN_PROGRESS:
                case PICKLIST_LOCKED:
                    switch (picklist.assignmentReason) {
                        case MANUAL_ASSIGNMENT:
                            metrics.manuallyAssignedPicklists++;
                            break;
                        case MANUALLY_PRIORITISED:
                            metrics.manuallyPrioritisedPicklists++;
                            break;
                        case OPTIMAL:
                        case TIME_CRITICAL:
                            metrics.optimallyAssignedPicklists++;
                            break;
                        default:
                            break;
                    }
                    break;
                default:
                    break;
            }
        });
        return metrics
    };

    const pickMetrics = getPickMetrics(routeList);
    const pickStatusInGridMetrics = getPickStatusInGridMetrics(picklistsInAisleGroups, picklistsInWorkGroups);

    const renderTotalsView = () => {
        return <PickStatusViewV2 data={{
            headerId: "pick.totals",
            defaultHeader: "Totals",
            value: pickMetrics.totalRoutes,
            footerId: "pickStatus.routes",
            defaultFooter: "Routes"
        }} />
    }

    const renderTotalPicklistsInGridView = () => {
        return <PickStatusViewV2 data={{
            headerId: "pick.totals",
            defaultHeader: "Totals",
            value: pickStatusInGridMetrics.availablePicklistsInGrid,
            footerId: "pickStatus.picklistsInGridMetricLabel",
            defaultFooter: "Picklists In Grid"
        }} />
    }

    const renderTotalPkgsView = () => {
        return <PickStatusViewV2 data={{
            headerId: "pickStatus.totalPackages",
            defaultHeader: "Total Packages",
            value: viewModel.totalPackages,
            footerId: "pick.packages",
            defaultFooter: "Packages"
        }} />
    }

    const renderNotReadyMetric = () => {
        return <ActionableMetricView
            onMetricCountClick={() => dispatchF({
                type: 'SELECT_ROUTE_STATUS',
                routeStatus: [PICK_STATUS_DYNAMIC.NOT_READY]
            })}
            pickStatus={{
                title: "pick.notReady",
                message: "Not ready",
                value: pickMetrics.routeNotReady
            }} />
    }

    const renderAvailableAssignmentMetric = () => {
        return <ActionableMetricView
            onMetricCountClick={() => dispatchF({
                type: 'SELECT_PICKLIST_STATUS_IN_GRID',
                picklistStatusInGrid: [PICK_STATUS_IN_GRID.AVAILABLE],
            })}
            pickStatus={{
                title: "pick.availablePicklistsMetricLabel",
                message: "Available For Assignment",
                value: pickStatusInGridMetrics.picklistsAvailableForAssignment
            }} />
    };

    const renderDwellingPicklistMetric = () => {
        return <ActionableMetricView
            onMetricCountClick={() => dispatchF({
                type: 'SELECT_PICKLIST_STATUS_IN_GRID',
                picklistStatusInGrid: [DWELLING_PICKLIST],
            })}
            pickStatus={{
                title: "pick.dwellingPicklistsMetricLabel",
                message: "Dwelling",
                value: pickStatusInGridMetrics.dwellingPicklists
            }} />
    };

    const renderBlockedAssignmentMetric = () => {
        return <ActionableMetricView
            onMetricCountClick={() => dispatchF({
                type: 'SELECT_PICKLIST_STATUS_IN_GRID',
                picklistStatusInGrid: [PICK_STATUS_IN_GRID.BLOCKED],
            })}
            pickStatus={{
                title: "pick.blockedPicklistsMetricLabel",
                message: "Assignment Blocked",
                value: pickStatusInGridMetrics.picklistsBlockedAssignment
            }} />
    };

    const renderManuallyPrioritisedMetric = () => {
        return <ActionableMetricView
            onMetricCountClick={() => dispatchF({
                type: 'SELECT_PICKLIST_STATUS_IN_GRID',
                picklistStatusInGrid: [PICK_STATUS_IN_GRID.MANUALLY_PRIORITISED]
            })}
            pickStatus={{
                title: "pick.manuallyPrioritisedPicklistsMetricLabel",
                message: "Manually Prioritised",
                value: pickStatusInGridMetrics.manuallyPrioritisedPicklists
            }} />
    };

    const renderManuallyAssignedMetric = () => {
        return <ActionableMetricView
            onMetricCountClick={() => dispatchF({
                type: 'SELECT_PICKLIST_STATUS_IN_GRID',
                picklistStatusInGrid: [PICK_STATUS_IN_GRID.MANUALLY_ASSIGNED]
            })}
            pickStatus={{
                title: "pick.manuallyAssignedPicklistsMetricLabel",
                message: "Manually Assigned",
                value: pickStatusInGridMetrics.manuallyAssignedPicklists
            }} />
    };

    const renderOptimallyAssignedMetric = () => {
        return <ActionableMetricView
            onMetricCountClick={() => dispatchF({
                type: 'SELECT_PICKLIST_STATUS_IN_GRID',
                picklistStatusInGrid: [PICK_STATUS_IN_GRID.OPTIMAL_ASSIGNMENT]
            })}
            pickStatus={{
                title: "pick.optimallyAssignedPicklistsMetricLabel",
                message: "Assigned Optimally",
                value: pickStatusInGridMetrics.optimallyAssignedPicklists
            }} />
    };

    const renderTimeCriticalMetric = () => {
        return <ActionableMetricView
            onMetricCountClick={() => dispatchF({
                type: 'SELECT_PICKLIST_STATUS_IN_GRID',
                picklistStatusInGrid: [PICK_STATUS_IN_GRID.TIME_CRITICAL]
            })}
            pickStatus={{
                title: "pick.timeCriticalPicklistsMetricLabel",
                message: "Time Critical",
                value: pickStatusInGridMetrics.timeCriticalPicklists
            }} />
    };

    const renderNotAssignedMetric = () => {
        if (configViewModel.stationConfig.parallelPickEnabled) {
            return <ActionableMetricPairView
                data={{
                    firstValue: pickMetrics.routeNotAssigned,
                    secondValue: pickMetrics.picklistNotAssigned,
                    onClickFirst: () => dispatchF({
                        type: 'SELECT_ROUTE_STATUS',
                        routeStatus: [PICK_STATUS.NOT_ASSIGNED]
                    }),
                    onClickSecond: () => dispatchF({
                        type: 'SELECT_PICKLIST_STATUS',
                        picklistStatus: [PICK_STATUS.NOT_ASSIGNED]
                    }),
                    footerId: "pick.notAssigned",
                    defaultFooter: "Not assigned",
                }}
            />
        }
        else if (configViewModel.capability === AR_V2) {
            return <StaticMetricView
                data={{
                    title: "pick.notAssigned",
                    message: "Not assigned",
                    value: viewModel.routeNotAssigned
                }} />
        } else {
            return <ActionableMetricView
                onMetricCountClick={() => dispatchF({
                    type: 'SELECT_ROUTE_STATUS',
                    routeStatus: [PICK_STATUS.NOT_ASSIGNED]
                })}
                pickStatus={{
                    title: "pick.notAssigned",
                    message: "Not assigned",
                    value: pickMetrics.routeNotAssigned
                }} />
        }
    }

    const renderInProgressMetric = () => {
        if (configViewModel.stationConfig.parallelPickEnabled) {
            return <ActionableMetricPairView
                data={{
                    firstValue: pickMetrics.routeInProgress,
                    secondValue: pickMetrics.picklistInProgress,
                    onClickFirst: () => dispatchF({
                        type: 'SELECT_ROUTE_STATUS',
                        routeStatus: [PICK_STATUS.IN_PROGRESS]
                    }),
                    onClickSecond: () => dispatchF({
                        type: 'SELECT_PICKLIST_STATUS',
                        picklistStatus: [PICK_STATUS.IN_PROGRESS]
                    }),
                    footerId: "pick.inProgress",
                    defaultFooter: "In progress",
                }}
            />
        } else if (configViewModel.capability === AR_V2) {
            return <StaticMetricView
                data={{
                    title: "pick.inProgress",
                    message: "In progress",
                    value: pickMetrics.routeInProgress
                }} />
        } else {
            return <ActionableMetricView
                onMetricCountClick={() => dispatchF({
                    type: 'SELECT_ROUTE_STATUS',
                    routeStatus: [PICK_STATUS.IN_PROGRESS]
                })}
                pickStatus={{
                    title: "pick.inProgress",
                    message: "In progress",
                    value: pickMetrics.routeInProgress
                }} />
        }
    }

    const renderPartiallyPickedMetric = () => {
        return <ActionableMetricView
            onMetricCountClick={() => dispatchF({
                type: 'SELECT_ROUTE_STATUS',
                routeStatus: [PICK_STATUS.PARTIALLY_PICKED]
            })}
            pickStatus={{
                title: "pickStatus.partiallyPicked",
                message: "Partially picked",
                value: pickMetrics.routePartiallyPicked
            }} />
    }

    const renderPickedMetric = () => {
        if (configViewModel.capability === AR_V2) {
            return <StaticMetricView
                data={{
                    title: "cartContent.picked",
                    message: "Picked",
                    value: pickMetrics.routePicked
                }} />
        }
        return <ActionableMetricView
            onMetricCountClick={() => dispatchF({type: 'SELECT_ROUTE_STATUS', routeStatus: [PICK_STATUS.PICKED]})}
            pickStatus={{
                title: "cartContent.picked",
                message: "Picked",
                value: pickMetrics.routePicked
            }} />
    }

    const renderRouteCutMetric = () => {
        return <StaticMetricView
            data={{
                title: "cartContent.routeCut",
                message: "Route Cut",
                value: pickMetrics.routeCut
            }} />
    }

    const renderSidelinedMetric = () => {
        return <StaticMetricView
            data={{
                title: "cartContent.routeCut",
                message: "Sidelined",
                value: pickMetrics.routeSidelined
            }} />
    }

    const renderDelayedPickMetric = () => {
        return <StaticMetricView
            data={{
                title: "outboundARV2.delayedPick",
                message: "Delayed Pick",
                value: pickMetrics.delayedPickRoutesARv2,
            }} />
    }

    const renderPickingMetricsView = () => {
        return <Box type="outline" spacingInset={"medium"}>
            <Column spacingInset={"none"}>
                {/* Title */}
                <Text type={headerFont}>
                    {viewState === PICK_VIEWS.LAYOUT_GRID_VIEW ?
                        <b>
                            <FormattedMessage id="pickStatus.picklistsInGrid" defaultMessage="Picklists in Grid" />
                        </b>
                        : configViewModel.stationConfig.parallelPickEnabled ?
                            <b>
                                <FormattedMessage id="pickStatus.routeOrPicklists" defaultMessage="Routes/Picklists" />
                            </b>
                            : <b>
                                <FormattedMessage id="pickStatus.routes" defaultMessage="Routes" />
                            </b>
                    }
                </Text>
                {/* Metrics */}
                <ScrollableElementWrapper>
                    {viewState === PICK_VIEWS.ROUTE_VIEW &&
                        <Row alignmentVertical="top" maxWidth="100%" width="100%" spacing={"medium"}>
                            {configViewModel.stationConfig.drsPickStageVisibilityEnabled && renderNotReadyMetric()}
                            {renderNotAssignedMetric()}
                            {renderInProgressMetric()}
                            {(configViewModel.capability === CORE_AMZL) && renderPartiallyPickedMetric()}
                            {renderPickedMetric()}
                            {(configViewModel.capability === AR_V2) && renderRouteCutMetric()}
                            {(configViewModel.capability === AR_V2) && renderSidelinedMetric()}
                            {(configViewModel.capability === AR_V2) && renderDelayedPickMetric()}
                        </Row>}
                    {viewState === PICK_VIEWS.LAYOUT_GRID_VIEW &&
                        <Row alignmentVertical="top" maxWidth="100%" width="100%" spacing={"medium"}>
                            {renderAvailableAssignmentMetric()}
                            {renderBlockedAssignmentMetric()}
                            {configViewModel.stationConfig.multiStationPicklistViewEnabled && renderDwellingPicklistMetric()}
                            {renderOptimallyAssignedMetric()}
                            {renderManuallyPrioritisedMetric()}
                            {renderManuallyAssignedMetric()}
                            {renderTimeCriticalMetric()}
                        </Row>}
                </ScrollableElementWrapper>
            </Column>
        </Box>
    }

    const renderDWPUploadView = () => {
        return <Column maxWidth="100%" spacingInset={"small"} spacing="none" backgroundColor="secondary">
            <Row spacing="none" wrap={"down"}>
                <Text type={headerFont}>
                    <b>
                        <FormattedMessage id="pick.dwpLoaded" defaultMessage="Dispatch plan" />:&nbsp;
                    </b>
                </Text>
                <Tooltip position="top" title={viewModelDWP.fileName}>
                    <Text type={textFont}>{viewModelDWP.fileName && viewModelDWP.fileName.length > 28 ?
                        viewModelDWP.fileName.substr(0, 28) + "..." : viewModelDWP.fileName}
                    </Text>
                </Tooltip>
            </Row>
            <Row spacing="none">
                <Text type={headerFont}>
                    <b>
                        <FormattedMessage id="pick.dwpTime" defaultMessage="Last update" />:&nbsp;
                    </b>
                </Text>
                <Text type={textFont}>{viewModelDWP.creationTime}</Text>
            </Row>
                <DWPUpload />
        </Column>
    }

    // Display alert if there are non-finalized routes for AMZL sites
    useEffect(() => {
        if (configViewModel.capability === CORE_AMZL && viewModelDWP.pendingPlanFinalization) {
            alertDispatch({
                type: "SHOW_NOTIFICATION", data: {
                    alert: {
                        title: (<FormattedMessage
                            id="pick.planFinalizationAlert"
                            values={{
                                link: <Link href={getDispatchPlanningURL()} rel={"noopener noreferrer"} target={"_blank"}>
                                    <FormattedMessage id="pick.planFinalizationAlert.link" defaultMessage="here"/>
                                </Link>
                            }}/>),
                        type: "warning"
                    }
                }
            });
        }
    }, [configViewModel.capability, viewModelDWP.pendingPlanFinalization])

    return (
        <Row maxWidth="100%" wrap={"down"} spacingInset="small none none none" alignmentVertical="stretch" >
            {viewState === PICK_VIEWS.LAYOUT_GRID_VIEW ? renderTotalPicklistsInGridView() : renderTotalsView()}
            {(configViewModel.capability !== AR_V2 && viewState === PICK_VIEWS.ROUTE_VIEW) ? <AveragePickDurationView routes={routeList} /> : null}
            {(configViewModel.capability === AR_V2) && renderTotalPkgsView()}
            {renderPickingMetricsView()}
            {(configViewModel.capability !== AR_V2 && viewState === PICK_VIEWS.ROUTE_VIEW) ? renderDWPUploadView() : null}
        </Row>
    )
}

export default PickOverAllStatusV2


// WEBPACK FOOTER //
// ./src/components/pickStatus/PickOverAllStatusV2.js